import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EntraGroupDto,
  OrganizationRoleType,
} from "../../../../autogen/bff-api";
import { useRoleName } from "./useRoleName";
import { selectableDepartments } from "../../../../common/departments/departmentVisibility";
import {
  entraDepartmentRoles,
  entraOrganizationRoles,
} from "./useSelectableRoles";

const ORGANIZATION_SCOPE = "organization";

interface Props {
  group: EntraGroupDto;
  departments: { id: string; name: string; active: boolean }[];
  isBusy: boolean;
  onSave: (
    role: OrganizationRoleType,
    departmentId: string | null,
  ) => Promise<void>;
  onClose: () => void;
}

export const EntraMappingModal = ({
  group,
  departments,
  isBusy,
  onSave,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const roleName = useRoleName();

  const [scope, setScope] = useState(
    group.mapping?.departmentId ?? ORGANIZATION_SCOPE,
  );
  const isOrganization = scope === ORGANIZATION_SCOPE;
  const roleOptions = isOrganization
    ? entraOrganizationRoles
    : entraDepartmentRoles;

  const [role, setRole] = useState<OrganizationRoleType>(
    group.mapping?.role ?? OrganizationRoleType.SourcingViewer,
  );
  const roleFitsScope = roleOptions.includes(role);

  const where = isOrganization
    ? t("the whole organization")
    : (departments.find((department) => department.id === scope)?.name ?? "");
  const wasDepartmentScoped = group.mapping?.departmentId != null;
  const choosesWhere = departments.length > 1 || wasDepartmentScoped;

  return (
    <Modal isOpen onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          {t("Give {{group}} a role", { group: group.displayName })}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex direction="column" gap="4">
            <Flex columnGap="6" rowGap="3" flexWrap="wrap">
              {choosesWhere && (
                <FormControl maxW="xs">
                  <FormLabel fontSize="xs" color="smMuted" mb="1">
                    {t("Where it applies")}
                  </FormLabel>
                  <Select
                    size="sm"
                    backgroundColor="smBackground"
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                  >
                    <option value={ORGANIZATION_SCOPE}>
                      {t("The whole organization")}
                    </option>
                    {selectableDepartments(departments).map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl maxW="xs">
                <FormLabel fontSize="xs" color="smMuted" mb="1">
                  {t("Role")}
                </FormLabel>
                <Select
                  size="sm"
                  backgroundColor="smBackground"
                  placeholder={
                    roleFitsScope
                      ? undefined
                      : (t("Choose a role") ?? undefined)
                  }
                  value={roleFitsScope ? role : ""}
                  onChange={(e) =>
                    setRole(e.target.value as OrganizationRoleType)
                  }
                >
                  {roleOptions.map((option) => (
                    <option key={option} value={option}>
                      {roleName(option)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Flex>

            {roleFitsScope && (
              <Text fontSize="sm">
                {t(
                  "Everyone Entra ID keeps in {{group}} will have the role {{role}} in {{where}}.",
                  {
                    group: group.displayName,
                    role: roleName(role),
                    where,
                  },
                )}
              </Text>
            )}

            {!isOrganization && (
              <Text fontSize="xs" color="smMuted">
                {t(
                  "A department role reaches only what belongs to this department.",
                )}
              </Text>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap="2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            size="sm"
            colorScheme="teal"
            isLoading={isBusy}
            isDisabled={!roleFitsScope}
            onClick={() => void onSave(role, isOrganization ? null : scope)}
          >
            {t("Give access")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
